import { io, type Socket } from "socket.io-client";
import { WS_URL, WS_EVENTS } from "./constants";
import { SecureTokenManager } from "./secure-token-manager";
import type {
	WSOrderUpdate,
	WSFraudAlert,
	WSSMSNotification,
	WSDeviceHeartbeat,
	WSDeviceOffline,
	WSRetroactiveMatch,
} from "@/types/api";

type WebSocketEventCallback = (data: any) => void;

export class WebSocketManager {
	private socket: Socket | null = null;
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 5;
	private reconnectDelay = 1000;
	private eventHandlers: Map<string, Set<WebSocketEventCallback>> = new Map();

	connect(): void {
		if (this.socket?.connected) {
			console.log("WebSocket already connected");
			return;
		}

		// Get JWT token from sessionStorage for authentication
		const token = SecureTokenManager.getToken();
		if (!token) {
			console.warn("No auth token found, skipping WebSocket connection");
			return;
		}

		// Connect with JWT token in auth object (backend expects socket.handshake.auth.token)
		this.socket = io(WS_URL, {
			auth: { token },
			withCredentials: false,
			transports: ["websocket"],
			reconnection: true,
			reconnectionAttempts: this.maxReconnectAttempts,
			reconnectionDelay: this.reconnectDelay,
		});

		this.setupEventHandlers();
	}

	private setupEventHandlers(): void {
		if (!this.socket) return;

		this.socket.on("connect", () => {
			console.log("WebSocket connected");
			this.reconnectAttempts = 0;
			this.emit("ws:connected", {});
		});

		this.socket.on("disconnect", (reason) => {
			console.log("WebSocket disconnected:", reason);
			this.emit("ws:disconnected", { reason });
		});

		this.socket.on("connect_error", (error) => {
			console.error("WebSocket connection error:", error);
			this.reconnectAttempts++;
			this.emit("ws:error", { error: error.message });

			if (this.reconnectAttempts >= this.maxReconnectAttempts) {
				console.error("Max reconnection attempts reached");
				this.disconnect();
			}
		});

		// Order updates
		this.socket.on(WS_EVENTS.ORDER_UPDATE, (data: WSOrderUpdate) => {
			this.emit(WS_EVENTS.ORDER_UPDATE, data);
		});

		// Fraud alerts
		this.socket.on(WS_EVENTS.FRAUD_ALERT, (data: WSFraudAlert) => {
			this.emit(WS_EVENTS.FRAUD_ALERT, data);
		});

		// SMS notifications
		this.socket.on(WS_EVENTS.SMS_NOTIFICATION, (data: WSSMSNotification) => {
			this.emit(WS_EVENTS.SMS_NOTIFICATION, data);
		});

		// Device heartbeat
		this.socket.on(WS_EVENTS.DEVICE_HEARTBEAT, (data: WSDeviceHeartbeat) => {
			this.emit(WS_EVENTS.DEVICE_HEARTBEAT, data);
		});

		// Device offline
		this.socket.on(WS_EVENTS.DEVICE_OFFLINE, (data: WSDeviceOffline) => {
			this.emit(WS_EVENTS.DEVICE_OFFLINE, data);
		});

		// Retroactive match
		this.socket.on(WS_EVENTS.RETROACTIVE_MATCH, (data: WSRetroactiveMatch) => {
			this.emit(WS_EVENTS.RETROACTIVE_MATCH, data);
		});
	}

	subscribe(channel: string): void {
		if (!this.socket?.connected) {
			console.warn("Cannot subscribe, WebSocket not connected");
			return;
		}

		this.socket.emit(WS_EVENTS.SUBSCRIBE, { channel });
		console.log(`Subscribed to channel: ${channel}`);
	}

	unsubscribe(channel: string): void {
		if (!this.socket?.connected) {
			return;
		}

		this.socket.emit(WS_EVENTS.UNSUBSCRIBE, { channel });
		console.log(`Unsubscribed from channel: ${channel}`);
	}

	on(event: string, callback: WebSocketEventCallback): void {
		if (!this.eventHandlers.has(event)) {
			this.eventHandlers.set(event, new Set());
		}
		this.eventHandlers.get(event)?.add(callback);
	}

	off(event: string, callback: WebSocketEventCallback): void {
		this.eventHandlers.get(event)?.delete(callback);
	}

	private emit(event: string, data: any): void {
		const handlers = this.eventHandlers.get(event);
		if (handlers) {
			handlers.forEach((handler) => handler(data));
		}
	}

	disconnect(): void {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
			this.eventHandlers.clear();
			console.log("WebSocket disconnected");
		}
	}

	isConnected(): boolean {
		return this.socket?.connected || false;
	}
}

// Singleton instance
export const wsManager = new WebSocketManager();
